/**
 * AvoCare Desktop — site vitrine
 * Navigation, animations (Motion), onglets, accordéon, formulaire de contact.
 * Les pages arabes sont pré-rendues au build (/ar/…) : aucune traduction à l'exécution.
 */
import { animate, stagger, inView } from 'motion';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isAr = document.documentElement.lang === 'ar';
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

document.documentElement.classList.add('js');

/* ── Compatibilité des anciennes URL ?lang=ar → /ar/ ─────────────── */
(function redirectLegacyLang() {
  const params = new URLSearchParams(location.search);
  if (params.get('lang') !== 'ar' || location.pathname.startsWith('/ar/')) return;
  const path = location.pathname === '/' || location.pathname === '/index.html' ? '/ar/' : `/ar${location.pathname}`;
  location.replace(path + location.hash);
})();

/* ── Barre de navigation ──────────────────────────────────────────── */
const nav = $('#nav');
if (nav) {
  const onScroll = () => nav.classList.toggle('scrolled', window.scrollY > 60);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

const navToggle = $('#nav-toggle');
const navLinks = $('#nav-links');
if (navToggle && navLinks) {
  const icon = navToggle.querySelector('.material-symbols-outlined');
  const setMenu = (open) => {
    navLinks.classList.toggle('open', open);
    navToggle.setAttribute('aria-expanded', String(open));
    if (icon) icon.textContent = open ? 'close' : 'menu';
    document.body.classList.toggle('menu-open', open);
  };
  navToggle.addEventListener('click', () => setMenu(!navLinks.classList.contains('open')));
  navLinks.addEventListener('click', (e) => { if (e.target.closest('a')) setMenu(false); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setMenu(false); });
  document.addEventListener('click', (e) => {
    if (navLinks.classList.contains('open') && !e.target.closest('#nav')) setMenu(false);
  });
}

/* ── Hero : progression de défilement (partagée avec le CSS et la scène 3D) ── */
const hero = $('#hero');
if (hero) {
  // Le navigateur émet des événements de défilement plus souvent qu'il ne rend
  // d'images. Chaque écriture de --hero-p invalide le style de six calques plein
  // écran : en les regroupant sur une image et en ignorant les variations
  // insignifiantes, on supprime autant de recalculs inutiles.
  let planifie = false, dernierP = -1;
  const appliquer = () => {
    planifie = false;
    const total = hero.offsetHeight - window.innerHeight;
    const top = hero.getBoundingClientRect().top;
    const p = total > 0 ? Math.min(1, Math.max(0, -top / total)) : 0;
    if (Math.abs(p - dernierP) < 0.002) return;
    dernierP = p;
    hero.style.setProperty('--hero-p', p.toFixed(3));
    hero.classList.toggle('is-faded', p > 0.4);
  };
  const update = () => {
    if (planifie) return;
    planifie = true;
    requestAnimationFrame(appliquer);
  };
  // La hauteur du hero est en vh. Sur mobile, ouvrir le clavier réduit la hauteur
  // de la fenêtre : le document raccourcissait d'un coup et la page remontait
  // pendant la saisie du formulaire. On fige la hauteur en pixels, et on ignore
  // les redimensionnements qui surviennent pendant qu'un champ a le focus.
  const figerHauteurHero = () => {
    hero.style.height = '';
    hero.style.height = `${Math.round(hero.getBoundingClientRect().height)}px`;
  };
  const saisieEnCours = () => {
    const el = document.activeElement;
    return !!el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName);
  };
  if (!reduceMotion) figerHauteurHero();

  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', () => {
    if (saisieEnCours()) return;
    if (!reduceMotion) figerHauteurHero();
    update();
  });
  update();

  // Scène 3D : chargée après le rendu de la page, hors du chemin critique (LCP),
  // seulement si le navigateur s'y prête (WebGL, pas de préférence "réduire les animations",
  // pas de mode économie de données, connexion et mémoire suffisantes).
  const canvas = $('#hero-canvas');
  // effectiveType n'est pas utilisé : certains navigateurs embarqués renvoient « slow-2g » à tort
  const conn = navigator.connection;
  const weak = !!(conn && conn.saveData) || (navigator.deviceMemory && navigator.deviceMemory < 2);
  if (canvas?.dataset.src && !reduceMotion && !weak) {
    let started = false;
    // Rendu logiciel : le navigateur calcule tout au processeur (pilote graphique
    // sur liste noire, accélération matérielle coupée, machine virtuelle). La scène
    // tournerait à quelques images par seconde — on garde l'image figée et on évite
    // les 520 Ko de three.js. Le test est fait ici, en temps mort, donc hors du
    // chemin critique.
    const renduLogiciel = () => {
      try {
        const probe = document.createElement('canvas');
        const gl = probe.getContext('webgl2') || probe.getContext('webgl');
        if (!gl) return true;
        const info = gl.getExtension('WEBGL_debug_renderer_info');
        const nom = info ? String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL) || '') : '';
        gl.getExtension('WEBGL_lose_context')?.loseContext();
        if (/swiftshader|llvmpipe|softpipe|basic render|software/i.test(nom)) {
          console.info('Scène 3D ignorée : rendu logiciel détecté (' + nom + ').');
          return true;
        }
        return false;
      } catch {
        return true;
      }
    };
    // ?debug3d dans l'URL : on laisse tourner quoi qu'il arrive, pour que le
    // panneau de diagnostic puisse afficher les mesures réelles de la machine.
    const diag = /(?:^|[?&#])debug3d/.test(location.search + location.hash);
    const go = () => {
      if (started) return;
      started = true;
      if (renduLogiciel() && !diag) return;
      import(canvas.dataset.src).catch((err) => console.warn('Scène 3D non chargée :', err));
    };
    const whenIdle = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 2500 });
      else setTimeout(go, 600);
    };
    const load = () => {
      if (window.matchMedia('(max-width: 900px)').matches) {
        // Mobile : l'image d'attente suffit au premier rendu ; three.js n'est chargé
        // qu'au premier geste (défilement, toucher) ou après 4 s, pour laisser le CPU
        // au contenu visible.
        const onGesture = () => { window.removeEventListener('scroll', onGesture); window.removeEventListener('pointerdown', onGesture); whenIdle(); };
        window.addEventListener('scroll', onGesture, { passive: true, once: true });
        window.addEventListener('pointerdown', onGesture, { passive: true, once: true });
        setTimeout(onGesture, 4000);
      } else {
        whenIdle();
      }
    };
    if (document.readyState === 'complete') load();
    else window.addEventListener('load', load, { once: true });
  }
}

/* ── Animations d'apparition (Motion) ─────────────────────────────── */
if (!reduceMotion) {
  const ease = [0.22, 1, 0.36, 1];

  if ($('.hero-content')) {
    animate('.hero-content > *', { opacity: [0, 1], y: [28, 0] }, { delay: stagger(0.11, { start: 0.25 }), duration: 0.85, easing: ease });
  }

  const revealGroup = (containerSel, itemSel, opts = {}) => {
    $$(containerSel).forEach((container) => {
      inView(container, () => {
        animate(container.querySelectorAll(itemSel), { opacity: [0, 1], y: [opts.y ?? 36, 0], ...(opts.scale ? { scale: [0.97, 1] } : {}) }, {
          delay: stagger(opts.stagger ?? 0.1), duration: opts.duration ?? 0.65, easing: ease,
        });
      }, { margin: '-12% 0px' });
    });
  };

  revealGroup('.features-grid', '[data-anim="card"]', { scale: true });
  revealGroup('.trust-grid', '[data-anim="card"]', { scale: true });
  revealGroup('.pricing-grid', '[data-anim="card"]', { scale: true, stagger: 0.12 });
  revealGroup('.section-header', '.section-tag, .section-title, .section-desc', { y: 24, duration: 0.6 });
  revealGroup('.security-list', 'li', { y: 0, stagger: 0.12, duration: 0.55 });
  revealGroup('.contact-info', '.contact-card', { y: 16, stagger: 0.12, duration: 0.55 });
  revealGroup('.cta-section', '.cta-badge, .cta-title, .cta-desc, .cta-buttons', { y: 24, stagger: 0.12 });
  revealGroup('.blog-grid', '.blog-card', { scale: true });

  $$('.visual-img, .preview-frame, .contact-form').forEach((el) => {
    let apparu = false;
    inView(el, () => {
      if (apparu) return; // sinon le formulaire se redécale à chaque retour dans le champ de vision
      apparu = true;
      animate(el, { opacity: [0, 1], y: [24, 0] }, { duration: 0.8, easing: ease });
    }, { margin: '-10% 0px' });
  });
} else {
  $$('[data-anim="card"], .hero-content > *, .visual-img, .preview-frame, .contact-form, .blog-card, .security-list li, .contact-card')
    .forEach((el) => { el.style.opacity = '1'; el.style.transform = 'none'; });
}

/* ── Pastille WhatsApp : apparaît une fois le hero passé ──────────── */
const waFab = $('#wa-fab');
if (waFab) {
  const heroSection = $('#hero');
  const showFab = () => {
    // Sur l'accueil on attend la fin de la séquence 3D ; ailleurs, un simple
    // défilement suffit à considérer que le visiteur est entré dans le contenu.
    const seuil = heroSection
      ? heroSection.offsetHeight - window.innerHeight * 1.05
      : 260;
    waFab.classList.toggle('is-visible', window.scrollY > Math.max(seuil, 120));
  };
  window.addEventListener('scroll', showFab, { passive: true });
  window.addEventListener('resize', showFab);
  showFab();
}

/* ── Onglets « Interface » ────────────────────────────────────────── */
const tabs = $$('.tab[role="tab"]');
const previewImg = $('#preview-img');
const previewAvif = $('#preview-avif');
const previewPanel = $('#preview-panel');
if (tabs.length && previewImg) {
  const select = (tab, focus = false) => {
    tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      t.classList.toggle('active', on);
    });
    if (previewPanel) previewPanel.setAttribute('aria-labelledby', tab.id);
    if (focus) tab.focus();

    const src = tab.dataset.src;
    if (!src || previewImg.dataset.src === src) return;
    previewImg.dataset.src = src;

    // On laisse <picture> choisir le format (AVIF si possible) puis on attend le
    // décodage de l'image réellement retenue : précharger nous-mêmes le WebP
    // téléchargerait un fichier que le navigateur n'affichera pas.
    previewImg.style.opacity = '0';
    requestAnimationFrame(() => {
      if (previewAvif && tab.dataset.avif) previewAvif.srcset = tab.dataset.avif;
      if (tab.dataset.webp) previewImg.srcset = tab.dataset.webp;
      previewImg.src = src;
      previewImg.alt = tab.dataset.alt || previewImg.alt;
      // Garde-fou : si decode() ne rend jamais la main (cas observé sur <picture>
      // quand la source change en cours de route), l'image resterait invisible.
      let revealed = false;
      const reveal = () => {
        if (revealed) return;
        revealed = true;
        clearTimeout(guard);
        previewImg.style.opacity = '1';
      };
      const guard = setTimeout(reveal, 700);
      previewImg.addEventListener('load', reveal, { once: true });
      if (previewImg.decode) previewImg.decode().then(reveal, reveal);
    });
  };

  tabs.forEach((tab) => tab.addEventListener('click', () => select(tab)));
  const tablist = tabs[0].closest('[role="tablist"]');
  tablist?.addEventListener('keydown', (e) => {
    const i = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    const step = { ArrowRight: 1, ArrowLeft: -1, Home: -i, End: tabs.length - 1 - i }[e.key];
    if (step === undefined) return;
    e.preventDefault();
    select(tabs[(i + step + tabs.length) % tabs.length], true);
  });
}

/* ── Tarifs : bascule mensuel / annuel ────────────────────────────── */
const billingToggle = $('.billing-toggle');
const pricingGrid = $('.pricing-grid');
if (billingToggle && pricingGrid) {
  const setCycle = (cycle) => {
    pricingGrid.dataset.cycle = cycle;
    $$('.bt-opt', billingToggle).forEach((b) => {
      const on = b.dataset.cycle === cycle;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', String(on));
    });
    $$('.plan-price[data-month]', pricingGrid).forEach((price) => {
      const amt = $('.plan-amt', price);
      if (amt) amt.textContent = cycle === 'year' ? price.dataset.year : price.dataset.month;
    });
  };
  billingToggle.addEventListener('click', (e) => {
    const btn = e.target.closest('.bt-opt');
    if (btn) setCycle(btn.dataset.cycle);
  });
}

/* ── Accordéon FAQ ────────────────────────────────────────────────── */
$$('.faq-item').forEach((item) => {
  const btn = item.querySelector('.faq-q');
  if (!btn) return;
  btn.addEventListener('click', () => {
    const open = item.classList.contains('open');
    $$('.faq-item.open').forEach((other) => {
      other.classList.remove('open');
      other.querySelector('.faq-q')?.setAttribute('aria-expanded', 'false');
    });
    if (!open) {
      item.classList.add('open');
      btn.setAttribute('aria-expanded', 'true');
    }
  });
});

/* ── Pré-remplissage du formulaire depuis les tarifs / démo / essai ── */
const periodeChoisie = () => {
  const cycle = $('.pricing-grid')?.dataset.cycle;
  return cycle === 'month' ? (isAr ? 'شهرية' : 'mensuelle') : (isAr ? 'سنوية' : 'annuelle');
};
// L'essai de 7 jours (D50.4) : un message prêt à envoyer, l'offre envisagée en plus.
const messageEssai = (plan) => {
  if (isAr) {
    return plan
      ? `أرغب في تجربة أفوكير مجاناً لمدة 7 أيام (العرض الذي يهمّني: ${plan}، بفوترة ${periodeChoisie()}).`
      : 'أرغب في تجربة أفوكير مجاناً لمدة 7 أيام.';
  }
  return plan
    ? `Je souhaite essayer AvoCare gratuitement pendant 7 jours (offre envisagée : ${plan}, en facturation ${periodeChoisie()}).`
    : 'Je souhaite essayer AvoCare gratuitement pendant 7 jours.';
};
$$('a[data-subject]').forEach((a) => {
  a.addEventListener('click', () => {
    const subject = $('#cf-subject');
    const message = $('#cf-message');
    if (subject) subject.value = a.dataset.subject;
    if (!message || message.value.trim()) return;
    if (a.dataset.subject === 'essai') {
      message.value = messageEssai(a.dataset.plan);
    } else if (a.dataset.plan) {
      message.value = isAr
        ? `أرغب في الحصول على وصول إلى صيغة ${a.dataset.plan} بفوترة ${periodeChoisie()}.`
        : `Je souhaite un accès à la formule ${a.dataset.plan}, en facturation ${periodeChoisie()}.`;
    }
  });
});

/* ── Sujet pré-sélectionné par l'URL (/contact.html?sujet=support) ─── */
// Permet de pointer depuis la FAQ ou une annonce directement sur le bon motif.
(function sujetDepuisUrl() {
  const demande = new URLSearchParams(location.search).get('sujet');
  const select = $('#cf-subject');
  if (!demande || !select) return;
  if ([...select.options].some((o) => o.value === demande)) select.value = demande;
  const message = $('#cf-message');
  if (demande === 'essai' && message && !message.value.trim()) message.value = messageEssai();
})();

/* ── Formulaire de contact ────────────────────────────────────────── */
const form = $('#contact-form');
if (form) {
  const btn = $('#cf-submit');
  const btnLabel = btn?.querySelector('.cf-submit-label');
  const success = $('#form-success');
  const errBox = $('#form-error');
  const errText = $('#form-error-text');
  const msg = (id) => $(`#${id}`)?.textContent?.trim() ?? '';
  const defaultError = errText?.textContent ?? '';
  const sendLabel = btnLabel?.textContent ?? '';
  const fields = {
    nom: $('#cf-name'), email: $('#cf-email'), telephone: $('#cf-phone'), societe: $('#cf-firm'),
    sujet: $('#cf-subject'), message: $('#cf-message'),
  };

  const setError = (input, text) => {
    if (!input) return;
    const box = document.getElementById(`${input.id}-err`);
    input.setAttribute('aria-invalid', text ? 'true' : 'false');
    if (box) box.textContent = text;
  };
  const validate = () => {
    let ok = true;
    let first = null;
    for (const input of [fields.nom, fields.email, fields.message]) {
      if (!input) continue;
      const value = input.value.trim();
      let text = '';
      if (!value) text = msg('i18n-err-required');
      else if (input.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) text = msg('i18n-err-email');
      setError(input, text);
      if (text) { ok = false; first ??= input; }
    }
    first?.focus();
    return ok;
  };
  Object.values(fields).forEach((input) => input?.addEventListener('input', () => setError(input, '')));

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    success?.classList.remove('visible');
    errBox?.classList.remove('visible');
    if (!validate()) return;

    btn.disabled = true;
    if (btnLabel) btnLabel.textContent = msg('i18n-sending') || sendLabel;

    const payload = {
      nom: fields.nom.value.trim(),
      email: fields.email.value.trim(),
      telephone: fields.telephone?.value.trim() || undefined,
      societe: fields.societe?.value.trim() || undefined,
      sujet: fields.sujet?.value || undefined,
      // La langue de la page décide de celle de l'email de confirmation
      lang: isAr ? 'ar' : 'fr',
      message: fields.message.value.trim(),
      type: 'contact',
      hp: $('#hp-field')?.value ?? '',
    };

    try {
      const res = await fetch(form.dataset.api || form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 422) {
        const data = await res.json().catch(() => ({}));
        const input = { nom: fields.nom, email: fields.email, message: fields.message }[data.field];
        if (input) { setError(input, data.error || msg('i18n-err-required')); input.focus(); }
        else throw new Error(data.error || `HTTP ${res.status}`);
        return;
      }
      if (res.status === 429) throw new Error(msg('i18n-err-rate') || defaultError);
      if (!res.ok) throw new Error(defaultError);

      form.reset();
      Object.values(fields).forEach((input) => setError(input, ''));
      success?.classList.add('visible');
      success?.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
    } catch (err) {
      if (errText) errText.textContent = err instanceof Error && err.message ? err.message : defaultError;
      errBox?.classList.add('visible');
    } finally {
      btn.disabled = false;
      if (btnLabel) btnLabel.textContent = sendLabel;
    }
  });
}
